import React, { useState, useLayoutEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { getAbrev } from '../utils/marca';

// Guía interactiva de primer ingreso (onboarding). Se abre sola la primera vez que
// entra un master (ver AppContext) y se puede reabrir desde el sidebar. Resalta un
// ítem del menú (por `data-tour`) con un spotlight + una tarjeta que explica cada
// módulo. El primer paso importante: registrar la marca en Administración.
// Es theme-aware (usa las variables CSS, se ve bien en claro y oscuro).
export default function OnboardingTour() {
  const { tourActivo, cerrarTour } = useApp();
  const navigate = useNavigate();
  const abrev = getAbrev();
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);

  const STEPS = [
    { title: '¡Te damos la bienvenida! 👋', body: 'En un minuto te muestro lo esencial para arrancar. Puedes saltar la guía cuando quieras.' },
    { target: 'admin', title: '1. Personaliza tu marca', body: 'Lo primero: entra a Administración → Marca del sistema y pon tu nombre, logo, colores, abreviatura y tipografía. Ahí el sistema se vuelve tuyo.' },
    { target: 'admin', title: '2. Crea tu equipo', body: 'En Administración → Usuarios agregas a tu gente y, con los roles, defines qué puede ver y hacer cada quien.' },
    { target: 'clientes', title: 'Clientes', body: 'Tu cartera de asegurados: datos, contactos y documentos. Todo empieza aquí.' },
    { target: 'polizas', title: 'Pólizas', body: 'Emite y administra las pólizas. Los cambios de riesgo (prima, coberturas, vigencia) se hacen por endoso.' },
    { target: 'solicitudes', title: 'Solicitudes', body: 'Cotizaciones, emisiones y endosos en proceso, hasta que se convierten en póliza.' },
    { target: 'cobros', title: 'Cobros', body: 'Registra los pagos, revisa estados de cuenta y controla la morosidad.' },
    { target: 'comisiones', title: 'Comisiones', body: `Lo que gana cada agente y tu ${abrev}, con su liquidación.` },
    { target: 'crm', title: 'CRM Gestión', body: 'Tu tablero del día: renovaciones, cobros y pendientes, priorizados.' },
    { target: 'reclamos', title: 'Reclamos', body: 'Gestiona los siniestros con la aseguradora, con sus documentos y coberturas.' },
    { title: '¡Todo listo! 🎉', body: 'También tienes Archivos, Agentes, Reportes y Contabilidad. Lo mejor es empezar registrando tu marca. Puedes reabrir esta guía cuando quieras desde el menú.', final: true },
  ];

  const paso = STEPS[idx] || STEPS[0];

  // Ubica el elemento resaltado (por data-tour) y guarda su posición.
  const medir = useCallback(() => {
    const key = STEPS[idx]?.target;
    if (!key) { setRect(null); return; }
    const el = document.querySelector(`[data-tour="${key}"]`);
    setRect(el ? el.getBoundingClientRect() : null);
  }, [idx]); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    if (!tourActivo) return;
    medir();
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, [tourActivo, idx, medir]);

  if (!tourActivo) return null;

  const total = STEPS.length;
  const esUltimo = idx >= total - 1;
  const siguiente = () => { if (esUltimo) cerrarTour(true); else setIdx(i => i + 1); };
  const anterior = () => setIdx(i => Math.max(0, i - 1));
  const saltar = () => cerrarTour(true);
  const irAMarca = () => { cerrarTour(true); navigate('/admin'); };

  // Posición de la tarjeta: a la derecha del ítem resaltado (el menú está a la
  // izquierda); si no hay objetivo, centrada.
  const cardW = 330;
  let cardStyle;
  if (rect) {
    const top = Math.max(12, Math.min(rect.top - 6, window.innerHeight - 250));
    let left = rect.right + 16;
    if (left + cardW > window.innerWidth - 12) left = Math.max(12, rect.left - cardW - 16);
    cardStyle = { position: 'fixed', top, left, width: cardW };
  } else {
    cardStyle = { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: cardW };
  }

  return (
    <>
      {/* Capa que bloquea la interacción con la app durante la guía. */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 3000 }} />

      {/* Spotlight: recorta y oscurece todo menos el ítem resaltado. */}
      {rect ? (
        <div style={{
          position: 'fixed', pointerEvents: 'none', zIndex: 3001,
          top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12,
          borderRadius: 10, border: '2px solid var(--sky)',
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)',
        }} />
      ) : (
        <div style={{ position: 'fixed', inset: 0, zIndex: 3001, pointerEvents: 'none', background: 'rgba(0,0,0,0.55)' }} />
      )}

      {/* Tarjeta del paso. */}
      <div style={{
        ...cardStyle, zIndex: 3002, background: 'var(--tour-card)', color: 'var(--text)',
        border: '1px solid var(--border)', borderRadius: 14, padding: 18,
        boxShadow: '0 12px 40px rgba(0,0,0,0.35)', fontFamily: 'var(--font)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 11, color: 'var(--silver)', fontWeight: 600 }}>Guía de inicio · {idx + 1}/{total}</span>
          <button onClick={saltar} style={{ background: 'none', border: 'none', color: 'var(--silver)', cursor: 'pointer', fontSize: 12 }}>Saltar</button>
        </div>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{paso.title}</div>
        <div style={{ fontSize: 13.5, lineHeight: 1.5, color: 'var(--text)', opacity: 0.9, marginBottom: 16 }}>{paso.body}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {idx > 0 && (
            <button onClick={anterior} style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 13, fontFamily: 'var(--font)' }}>Anterior</button>
          )}
          <div style={{ flex: 1 }} />
          {paso.final ? (
            <>
              <button onClick={() => cerrarTour(true)} style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 13, fontFamily: 'var(--font)' }}>Entendido</button>
              <button onClick={irAMarca} style={{ background: 'var(--sky)', border: 'none', color: '#fff', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'var(--font)' }}>Registrar mi marca</button>
            </>
          ) : (
            <button onClick={siguiente} style={{ background: 'var(--sky)', border: 'none', color: '#fff', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'var(--font)' }}>Siguiente</button>
          )}
        </div>
      </div>
    </>
  );
}
